import React, { useState } from "react";
import { motion } from "framer-motion";
import { UserPlus, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";

const StudentOnboarding = ({ user, onComplete }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    age: "",
    gender: "",
    email: "",
    phone: user.phone || "",
    parent_name: "",
    parent_phone: "",
  });

  const [errors, setErrors] = useState({});

  const validateMobileNumber = (number) => {
    const phonePattern = /^[6-9]\d{9}$/;
    return phonePattern.test(number);
  };

  const validateEmail = (email) => {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  };

  const validateStep1 = () => {
    const newErrors = {};
    
    if (!formData.first_name.trim()) {
      newErrors.first_name = "First name is required";
    }
    
    if (!formData.last_name.trim()) {
      newErrors.last_name = "Last name is required";
    }
    
    if (!formData.age) {
      newErrors.age = "Age is required";
    } else if (parseInt(formData.age) < 5 || parseInt(formData.age) > 100) {
      newErrors.age = "Age must be between 5 and 100";
    }
    
    if (!formData.gender) {
      newErrors.gender = "Please select gender";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    
    if (!formData.phone) {
      newErrors.phone = "Mobile number is required";
    } else if (!validateMobileNumber(formData.phone)) {
      newErrors.phone = "Invalid mobile number. Must be 10 digits starting with 6-9";
    }
    
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Invalid email address";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep3 = () => {
    const newErrors = {};
    
    if (!formData.parent_name.trim()) {
      newErrors.parent_name = "Parent/Guardian name is required";
    }
    
    if (!formData.parent_phone) {
      newErrors.parent_phone = "Parent/Guardian mobile number is required";
    } else if (!validateMobileNumber(formData.parent_phone)) {
      newErrors.parent_phone = "Invalid mobile number. Must be 10 digits starting with 6-9";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep3()) return;

    setLoading(true);
    try {
      const response = await api.put("/users/profile", {
        ...formData,
        age: parseInt(formData.age),
      });

      toast.success("Profile completed successfully!");
      
      // Update local storage with new user data
      localStorage.setItem("user", JSON.stringify(response.data.user));
      
      // Call onComplete callback
      onComplete(response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-600 to-pink-500 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-2xl"
      >
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-violet-600 to-pink-500 p-8 text-center">
            <UserPlus className="w-16 h-16 text-white mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-white font-heading" data-testid="onboarding-title">
              Complete Your Profile
            </h1>
            <p className="text-white/90 text-sm mt-2">Help us know you better!</p>
            
            {/* Progress Bar */}
            <div className="flex items-center justify-center gap-2 mt-6">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`h-2 rounded-full transition-all ${
                    s <= step ? "bg-white w-16" : "bg-white/30 w-8"
                  }`}
                />
              ))}
            </div>
            <p className="text-white/80 text-xs mt-2">Step {step} of 3</p>
          </div>

          {/* Form */}
          <div className="p-8">
            {/* Step 1: Basic Info */}
            {step === 1 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4"
              >
                <h2 className="text-2xl font-bold font-heading text-slate-900 mb-6">Basic Information</h2>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first-name">First Name *</Label>
                    <Input
                      id="first-name"
                      data-testid="first-name-input"
                      placeholder="Enter first name"
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      className={`h-12 rounded-xl ${errors.first_name ? "border-red-500" : ""}`}
                    />
                    {errors.first_name && (
                      <p className="text-xs text-red-600">{errors.first_name}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="last-name">Last Name *</Label>
                    <Input
                      id="last-name"
                      data-testid="last-name-input"
                      placeholder="Enter last name"
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      className={`h-12 rounded-xl ${errors.last_name ? "border-red-500" : ""}`}
                    />
                    {errors.last_name && (
                      <p className="text-xs text-red-600">{errors.last_name}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="age">Age *</Label>
                    <Input
                      id="age"
                      data-testid="age-input"
                      type="number"
                      placeholder="Enter age"
                      value={formData.age}
                      onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                      className={`h-12 rounded-xl ${errors.age ? "border-red-500" : ""}`}
                    />
                    {errors.age && (
                      <p className="text-xs text-red-600">{errors.age}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="gender">Gender *</Label>
                    <select
                      id="gender"
                      data-testid="gender-select"
                      value={formData.gender}
                      onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                      className={`w-full h-12 rounded-xl border px-4 bg-white ${
                        errors.gender ? "border-red-500" : "border-slate-200"
                      }`}
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Others">Others</option>
                    </select>
                    {errors.gender && (
                      <p className="text-xs text-red-600">{errors.gender}</p>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: Contact Info */}
            {step === 2 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4"
              >
                <h2 className="text-2xl font-bold font-heading text-slate-900 mb-6">Contact Information</h2>
                
                <div className="space-y-2">
                  <Label htmlFor="phone">Mobile Number *</Label>
                  <Input
                    id="phone"
                    data-testid="phone-input"
                    type="tel"
                    placeholder="10-digit mobile number"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className={`h-12 rounded-xl ${errors.phone ? "border-red-500" : ""}`}
                    maxLength={10}
                  />
                  {errors.phone && (
                    <p className="text-xs text-red-600">{errors.phone}</p>
                  )}
                  <p className="text-xs text-slate-500">Must start with 6, 7, 8, or 9</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    data-testid="email-input"
                    type="email"
                    placeholder="your.email@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`h-12 rounded-xl ${errors.email ? "border-red-500" : ""}`}
                  />
                  {errors.email && (
                    <p className="text-xs text-red-600">{errors.email}</p>
                  )}
                </div>
              </motion.div>
            )}

            {/* Step 3: Parent Info */}
            {step === 3 && (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-4"
              >
                <h2 className="text-2xl font-bold font-heading text-slate-900 mb-6">Parent/Guardian Information</h2>
                
                <div className="space-y-2">
                  <Label htmlFor="parent-name">Parent/Guardian Name *</Label>
                  <Input
                    id="parent-name"
                    data-testid="parent-name-input"
                    placeholder="Enter parent or guardian name"
                    value={formData.parent_name}
                    onChange={(e) => setFormData({ ...formData, parent_name: e.target.value })}
                    className={`h-12 rounded-xl ${errors.parent_name ? "border-red-500" : ""}`}
                  />
                  {errors.parent_name && (
                    <p className="text-xs text-red-600">{errors.parent_name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="parent-phone">Parent/Guardian Mobile Number *</Label>
                  <Input
                    id="parent-phone"
                    data-testid="parent-phone-input"
                    type="tel"
                    placeholder="10-digit mobile number"
                    value={formData.parent_phone}
                    onChange={(e) => setFormData({ ...formData, parent_phone: e.target.value })}
                    className={`h-12 rounded-xl ${errors.parent_phone ? "border-red-500" : ""}`}
                    maxLength={10}
                  />
                  {errors.parent_phone && (
                    <p className="text-xs text-red-600">{errors.parent_phone}</p>
                  )}
                  <p className="text-xs text-slate-500">Must start with 6, 7, 8, or 9</p>
                </div>
              </motion.div>
            )}

            {/* Navigation Buttons */}
            <div className="flex gap-4 mt-8">
              {step > 1 && (
                <Button
                  data-testid="back-button"
                  onClick={() => setStep(step - 1)}
                  variant="outline"
                  className="flex-1 h-12 rounded-full"
                >
                  Back
                </Button>
              )}
              
              {step < 3 ? (
                <Button
                  data-testid="next-button"
                  onClick={handleNext}
                  className="flex-1 h-12 rounded-full bg-primary hover:bg-primary/90 font-medium"
                >
                  Next
                </Button>
              ) : (
                <Button
                  data-testid="complete-button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 h-12 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 text-white hover:opacity-90 font-medium"
                >
                  {loading ? (
                    "Completing..."
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5 mr-2" />
                      Complete Profile
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default StudentOnboarding;
